import { Skeleton, Stack } from '@mui/material'
import { SkeletonText } from 'utoniq-ui'

import {
  ArtistPreSaleLotteryApplicantDetailFragment,
  Maybe,
  PaymentMethod,
} from '@/clients/utoniq-core/schema'
import { BaseLayout, Head } from '@/components/layouts'
import { ArtistThemeProvider } from '@/components/styles'
import { useRouter } from '@/core'
import { CellFrame, Template } from '@/core/architecture'

import {
  PaymentInfoCell,
  TicketDetailsCell,
  TotalPaymentAmountCell,
  UserInfoCell,
} from './components'

export * from './fragments'

export type State = {
  artistId: string
  preSaleLotteryApplicant: Maybe<ArtistPreSaleLotteryApplicantDetailFragment>
}

/**
 * Action type.
 * use key name with 'on' prefix like onLogin.
 */
export type Action = {}

export const ConsoleLotteryManagementDetailTemplate: Template<State, Action> =
  ({ state }) => {
    const { routerNames, routerPages, generatePath, router } = useRouter<{
      preSaleId: string
    }>()
    return (
      <ArtistThemeProvider artistId={state.artistId}>
        <Head title={routerNames.ConsoleLotteryManagementDetail} />
        <BaseLayout
          margin
          header={{
            back: true,
            defaultBackPage: generatePath(
              routerPages.ConsoleLotteryManagements,
              {
                preSaleId: router.query.preSaleId ?? '#',
              }
            ),
            title: state.preSaleLotteryApplicant?.preSale.title,
          }}
        >
          {state.preSaleLotteryApplicant ? (
            <>
              <TicketDetailsCell
                choiceItemDetails={
                  state.preSaleLotteryApplicant.choiceItemDetails
                }
                winChoiceNumber={state.preSaleLotteryApplicant.winChoiceNumber}
                status={state.preSaleLotteryApplicant.status}
              />
              <PaymentInfoCell
                isKonbini={
                  state.preSaleLotteryApplicant.paymentMethod ===
                  PaymentMethod.Konbini
                }
              />
              <TotalPaymentAmountCell
                choiceItemDetails={
                  state.preSaleLotteryApplicant.choiceItemDetails
                }
                winChoiceNumber={state.preSaleLotteryApplicant.winChoiceNumber}
              />
              <UserInfoCell user={state.preSaleLotteryApplicant.user} />
            </>
          ) : (
            <>
              <TicketDetailsSkeletonCell />
              <PaymentInfoSkeletonCell />
              <TotalPaymentAmountSkeletonCell />
              <UserInfoSkeletonCell />
            </>
          )}
        </BaseLayout>
      </ArtistThemeProvider>
    )
  }

const TicketDetailsSkeletonCell = () => {
  return (
    <CellFrame sx={{ pb: 5 }}>
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        py={3}
      >
        <SkeletonText variant="large" width="50%" />
      </Stack>
      <Stack spacing={3} sx={{ bgcolor: 'grey.50', p: 5, borderRadius: 2 }}>
        <SkeletonText variant="medium" />
        <Stack>
          <SkeletonText variant="medium" numberOfLine={5} />
        </Stack>
        <Skeleton
          variant="rectangular"
          width="100%"
          height={44}
          sx={{ borderRadius: '34px' }}
        />
      </Stack>
    </CellFrame>
  )
}

const PaymentInfoSkeletonCell = () => {
  return (
    <CellFrame sx={{ pb: 5 }}>
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        py={3}
      >
        <SkeletonText variant="large" width="50%" />
      </Stack>
      <Stack sx={{ bgcolor: 'grey.50', p: 5, borderRadius: 2 }}>
        <Skeleton variant="rectangular" width="50%" height={32} />
      </Stack>
    </CellFrame>
  )
}

const TotalPaymentAmountSkeletonCell = () => {
  return (
    <CellFrame sx={{ pb: 5 }}>
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        py={3}
      >
        <SkeletonText variant="large" width="50%" />
      </Stack>
      <Stack sx={{ bgcolor: 'grey.50', p: 5, borderRadius: 2 }}>
        <Stack direction="row" spacing={2}>
          <Stack flex={2}>
            <SkeletonText variant="small" />
          </Stack>
          <Stack flex={1}>
            <SkeletonText variant="small" />
          </Stack>
        </Stack>
        <Stack direction="row" spacing={2}>
          <Stack flex={2}>
            <SkeletonText variant="small" />
          </Stack>
          <Stack flex={1}>
            <SkeletonText variant="small" />
          </Stack>
        </Stack>
      </Stack>
    </CellFrame>
  )
}

const UserInfoSkeletonCell = () => {
  return (
    <CellFrame sx={{ pb: 5 }}>
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        py={3}
      >
        <SkeletonText variant="large" width="50%" />
        <SkeletonText variant="large" width="30%" />
      </Stack>
      <Stack spacing={2} sx={{ bgcolor: 'grey.50', p: 5, borderRadius: 2 }}>
        {Array.from(Array(3).keys()).map(index => (
          <Stack spacing={2} key={index}>
            <Stack>
              <SkeletonText variant="small" width="10%" />
            </Stack>
            <Stack>
              <SkeletonText variant="small" />
            </Stack>
          </Stack>
        ))}
      </Stack>
    </CellFrame>
  )
}
